import React, {createRef, useContext, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import {TablePagination, useTheme} from "@material-ui/core";

import {
  useTripUldStacks,
  useAddTripUldStack,
  useUpdateTripUldStack,
  useDeleteTripUldStack,
} from "../../../services/trips/";
import AirportLookup from "../../Airports/components/AirportsLookup";
import {ValidatorColumn, ColumnWidth} from "../../../blocks/ColumnHelper";
import TripUldStacksValidator from "../validators/TripUldStacksValidator";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { MaskedNumber, UldStackSelect, UldEquipmentSelect } from '../../../controls';
import SystemContext from '../../../SystemContext';
import NotesActionButton from '../../../controls/Notes/NotesActionButton';
import NotesDialog from '../../../controls/Notes/NotesDialog';

const TripUldStacks = (_props) => {
  const {isAuthenticated} = useAuth0();

  const props = {
    canEdit: true,
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit
  });

  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const eventBus = SystemData.eventBus;
  useEffect(() => {
    const handleAddRow = () => {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
    }
    if (eventBus) {
      eventBus.bind('TripUldStacks.Add', handleAddRow);
      return ()=>{
        eventBus.unbind('TripUldStacks.Add', handleAddRow);
      }
    }  
  }, [eventBus, tableRef])

  const tripUuid = props.tripUuid;
  const tripRoute = props.tripRoute;

  const handleAddTripUldStack = useAddTripUldStack();
  const handleUpdateTripUldStack = useUpdateTripUldStack();
  const handleDeleteTripUldStack = useDeleteTripUldStack();

  const extraKeys = [tripUuid];
  const queryClient = useQueryClient();
  const tripUldStacks = useTripUldStacks({tableRef, queryClient, extraKeys});

  const getTripUldStackColumns = (props) => {
    const baseColumn = {
      cellStyle: {padding: "8px 2px"},
      headerStyle: {padding: "8px 2px"},
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const additionalValues = {route: tripRoute};
    const validatorColumn = ValidatorColumn(TripUldStacksValidator, false, additionalValues);

    return [
      {...validatorColumn},
      {
        ...baseColumn,
        ...ColumnWidth(100,null,null,{padding: "8px 2px 8px 12px"},{padding: "8px 2px 8px 12px"}),
        title: "Origin",
        field: "origin",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Destination",
        field: "destination",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        title: "ULD Stack",
        field: "uldStack",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <UldStackSelect
            value={props.value}
            allowZero={true}
            onChange={(record) => {
              let newRowData = {...props.rowData, ...record};
              const {uldStack, equipment} = newRowData;
              let stackWeightKg = 0;
              (uldStack||[]).forEach(record => {
                const {quantity, tareWeight} = record;
                stackWeightKg += ((quantity||0) * (tareWeight||0))
              });
              (equipment||[]).forEach(record => {
                const {quantity, unitWeight} = record;
                stackWeightKg += ((quantity||0) * (unitWeight||0))
              });
              newRowData = {...newRowData, stackWeightKg};
              props.onRowDataChange(newRowData);              
            }}
          />
        ),
        render: (rowdata) => (
          <UldStackSelect            
            value={rowdata.uldStack}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        title: "ULD Equipment",
        field: "equipment",
        initialEditValue: "",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <UldEquipmentSelect
            value={props.value}            
            allowZero={true}
            onChange={(record) => {
              let newRowData = {...props.rowData, ...record};
              const {uldStack, equipment} = newRowData;
              let stackWeightKg = 0;
              (uldStack||[]).forEach(record => {
                const {quantity, tareWeight} = record;
                stackWeightKg += ((quantity||0) * (tareWeight||0))
              });
              (equipment||[]).forEach(record => {
                const {quantity, unitWeight} = record;
                stackWeightKg += ((quantity||0) * (unitWeight||0))
              });
              stackWeightKg = Math.ceil(stackWeightKg);
              newRowData = {...newRowData, stackWeightKg};
              props.onRowDataChange(newRowData);              
            }}
          />
        ),
        render: (rowdata) => (
          <UldEquipmentSelect            
            value={rowdata.equipment}
            readOnly={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(160,undefined, undefined, {paddingRight: 10}, {paddingRight: 10}, {paddingRight: 10}, ),
        title: "Weight KG",
        field: "stackWeightKg",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (rowdata) => (
          <MaskedNumber
            displayType="text"
            value={rowdata.stackWeightKg}
            fixedDecimalScale={true}
            readOnly={true}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{marginLeft: props.mode === "DIALOG"?0:50}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
          padding: 'dense',
          toolbar: props.mode !== "DIALOG",
          search: props.mode !== "DIALOG",
          showTitle: props.mode !== "DIALOG",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          (rowData) => (
            {
              hidden: false,
              icon: () => (
                <NotesActionButton 
                  canEdit={true}
                  primaryObjectUuid={rowData.tripUldStackUuid}
                  noteType="TRIP:ULD_STACKS"
                  title="ULD Stack Note"
                  eventPrefix="UldStackNoteDialog"                  
                  style={{ color: theme.palette.primary.main, padding: 0 }}
                />
              ),
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
            }
          ),  
        ]}
        title="Trip ULD Replenishment"
        columns={getTripUldStackColumns({canEdit: state.canEdit})}
        data={tripUldStacks.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              handleAddTripUldStack.mutate({record: {...newData, tripUuid: tripUuid}, resolve, reject, extraKeys});
            })
            : undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateTripUldStack.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteTripUldStack.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />
      <NotesDialog 
        eventPrefix="UldStackNoteDialog"
        afterSave={undefined}
        onClose={undefined}
        classes={{}}
      />
    </div>
  );

}

export default TripUldStacks;
